import { useEffect, useMemo, useState } from "react";
import { CrossIcon, UserIcon, UserPlusIcon } from "../Icons";
import CustomModal from "../DealTracker/CustomModal";
import ContactForm from "./ContactForm";
import { useGetVendorContactsQuery } from "../../store/apiSlices/vendorContacts";
import { useSearchParams } from "react-router-dom";

export default function VendorContacts() {
  const [active, setActive] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [query, setQuery] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const activeContactId = searchParams.get("contactId");

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const { data: contacts, isLoading, isError } = useGetVendorContactsQuery();

  useEffect(() => {
    if (activeContactId) {
      setActive(Number(activeContactId));
    }
  }, [activeContactId]);

  // Filter contacts based on query
  const filteredContacts = useMemo(() => {
    if (!contacts) return undefined;
    if (!query.trim()) return contacts;

    const lower = query.toLowerCase();
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(lower) ||
        c?.company?.toLowerCase().includes(lower),
    );
  }, [query, contacts]);

  const handleContactClick = (contactId: number, hashId: string) => {
    setActive(contactId);
    setSearchParams({ contactId: String(contactId), hashId });
  };

  return (
    <>
      <aside className="w-[350px] border-r border-gray-200 bg-white flex flex-col">
        <header className="px-4 py-3 flex items-center gap-[10px] border-b border-gray-200 font-semibold text-gray-700">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search vendors..."
            className="input-new"
          />
          <button
            type="button"
            onClick={toggleModal}
            className="p-1 rounded-lg hover:bg-gray-100 hover:scale-95 hover:text-custom-blue-medium"
          >
            <UserPlusIcon />
          </button>
        </header>

        {isLoading && <ContactListSkeleton />}

        {isError && (
          <p className="text-center text-sm text-gray-400 my-4">
            Something went wrong!
          </p>
        )}

        {!isLoading && !isError && (
          <ul className="flex-1 overflow-y-auto will-change-scroll">
            {filteredContacts && filteredContacts.length > 0 ? (
              filteredContacts.map((c) => (
                <li
                  key={c.id}
                  onClick={() => handleContactClick(c.id, c.hash_id)}
                  className={`cursor-pointer px-4 py-3 flex items-center gap-3 border-b border-gray-100 
              hover:bg-gray-50 transition 
                  ${
                    active === c.id
                      ? "bg-blue-50 border-l-4 border-l-custom-blue-medium/20"
                      : ""
                  }`}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200">
                    {/* <User className="w-5 h-5 text-gray-600" /> */}
                    <UserIcon className="size-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{c.name}</p>
                    <p className="text-xs text-gray-500">{c.email}</p>
                    {/* <p className="text-[10px] text-gray-500 truncate">
                      {c.lastMessage || "No recent messages"}
                    </p> */}
                  </div>
                  {/* <span className="text-xs text-gray-400">
                    {c.created_at.split(" ").at(1)}
                  </span> */}
                </li>
              ))
            ) : (
              <p className="text-center text-gray-500 text-sm mt-6">
                No contacts found
              </p>
            )}
          </ul>
        )}
      </aside>
      <CustomModal
        open={isModalOpen}
        onClose={toggleModal}
        className="bg-white rounded-xl shadow-xl p-6 max-w-xl w-full "
      >
        <button onClick={toggleModal} className="float-end hover:scale-95">
          <CrossIcon className="size-6" />
        </button>
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">Add Contact</h2>
          <ContactForm toggleModal={toggleModal} />
        </div>
      </CustomModal>
    </>
  );
}

function ContactListSkeleton() {
  return (
    <div className="space-y-4 p-4 w-[350px]">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 animate-pulse">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-gray-300" />

          {/* Text lines */}
          <div className="flex-1 space-y-2">
            <div className="w-1/3 h-3 bg-gray-300 rounded" />
            <div className="w-1/2 h-3 bg-gray-200 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
